import React, { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import InputField from "../../components/InputComponent/InputField";
import "react-toastify/dist/ReactToastify.css";
import GeneralTable from "../../components/Table/GeneralTable";
import IconButton from "../../components/IconButton/IconButton";
import "./ManageAttendance.scss";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import StatusCard from "../../components/StatusCard/StatusCard";
import { useAuth } from "../../contexts/AuthContext";
import {
  MiniLocationMap,
  LocationModal,
} from "../../components/LocationMap/LocationMap";
import { Icon } from "@iconify/react";
import EnhancedAttendanceCalendar from "./AttendanceCalendar";
import EnhancedReusableCalendar from "../../components/AttendanceCalender/ReusableCalendar";
import { exportCalendarToExcel } from "../../utils/exportCalendarToExcel";
import WarningPopup from "../../components/WarningPopup/WarningPopup";

function ManageAttendance({ onRequestProcessed, onNotificationUpdate }) {
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [roleNameFilter, setRoleNameFilter] = useState("");
  const [attendanceTypeFilter, setAttendanceTypeFilter] = useState("");
  const [punchStatusFilter, setPunchStatusFilter] = useState("all");
  const [quickRange, setQuickRange] = useState("all"); // all | wtd | mtd | ytd | custom
  const [use24Hour, setUse24Hour] = useState(false);
  const [data, setData] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [viewReport, setViewReport] = useState(false);
  const [viewSummary, setViewSummary] = useState(false);
  const [viewCalendar, setViewCalendar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [singleEmployeeData, setSingleEmployeeData] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("all");
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [selectedRole, setSelectedRole] = useState("");
  const [availableRoles, setAvailableRoles] = useState([]);
  const { user } = useAuth();

  // Reused by both the punchout table row action and the calendar views —
  // only Admin/HR are allowed to delete attendance records.
  const canDeleteAttendance =
    user?.authorities?.includes("ROLE_ADMIN") ||
    user?.authorities?.includes("ROLE_HR");

  // Bulk selection state
  const [selectedAttendance, setSelectedAttendance] = useState(new Set());

  // Calendar state (used ONLY by the Calendar tab / EnhancedAttendanceCalendar)
  const [calendarData, setCalendarData] = useState({});
  const [employeeList, setEmployeeList] = useState([]);

  // Report-tab's own internal grouping (used only for report-side bookkeeping).
  // IMPORTANT: kept separate from calendarData/employeeList above — those
  // entries never carry workingDays/monthlyAttendance, so feeding them into
  // the Calendar tab silently breaks weekly-off detection (Saturdays show as
  // "Absent" instead of "Weekly Off" until the Calendar tab re-fetches).
  const [reportCalendarData, setReportCalendarData] = useState({});
  const [reportEmployeeList, setReportEmployeeList] = useState([]);

  // Guards against out-of-order async responses: if the user flips through
  // months quickly, an older, slower request can resolve after a newer one
  // and overwrite state with the wrong month's data. Each fetch stamps the
  // request it belongs to and only applies its result if it's still current.
  const calendarRequestId = useRef(0);
  const singleEmployeeRequestId = useRef(0);

  // Modal state for editing times
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editPunchOutTime, setEditPunchOutTime] = useState("");
  const [editPunchInTime, setEditPunchInTime] = useState("");
  const [editMode, setEditMode] = useState("punchout");
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocationRecord, setSelectedLocationRecord] = useState(null);

  const formatTime12Hour = (hour, minute) => {
    if (typeof hour !== "number" || typeof minute !== "number") {
      return "—";
    }

    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      return "—";
    }

    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const formattedHour = displayHour.toString().padStart(2, "0");
    const formattedMinute = minute.toString().padStart(2, "0");
    return `${formattedHour}:${formattedMinute} ${period}`;
  };

  const timeObjectToString = (timeObj) => {
    if (
      !timeObj ||
      typeof timeObj.hour !== "number" ||
      typeof timeObj.minute !== "number"
    ) {
      return "";
    }
    return `${timeObj.hour.toString().padStart(2, "0")}:${timeObj.minute
      .toString()
      .padStart(2, "0")}`;
  };

  const timeStringToObject = (timeString) => {
    if (!timeString) return { hour: 0, minute: 0 };
    const [hour, minute] = timeString
      .split(":")
      .map((num) => parseInt(num) || 0);
    return { hour, minute };
  };

  const handleLocationClick = (record) => {
    setSelectedLocationRecord(record);
    setShowLocationModal(true);
  };

  const decreaseNotificationCount = (count = 1) => {
    if (onNotificationUpdate && typeof onNotificationUpdate === "function") {
      onNotificationUpdate(-count);
    }
  };

  useEffect(() => {
    const fetchPunchoutRequests = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_MANAGER_URL}/attendance/punchout-requests`,
        );

        const transformedData = transformAttendanceData(
          response.data.data || [],
        );
        setData(transformedData);
        setSelectedAttendance(new Set()); // Clear selection when data loads
      } catch (error) {
        const status = error?.response?.status;
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to fetch attendance requests";

        setData([]);

        console.log("Error fetching punchout requests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPunchoutRequests();
  }, []);

  useEffect(() => {
    if (!user) return;

    const roles = [];

    if (user.authorities?.includes("ROLE_ADMIN")) {
      roles.push("ADMIN");
    }
    if (user.authorities?.includes("ROLE_HR")) {
      roles.push("HR");
    }
    if (user.authorities?.includes("ROLE_MANAGER")) {
      roles.push("MANAGER");
    }

    setAvailableRoles(roles);

    if (roles.includes("ADMIN")) {
      setSelectedRole("ADMIN");
    } else if (roles.includes("HR")) {
      setSelectedRole("HR");
    } else if (roles.includes("MANAGER")) {
      setSelectedRole("MANAGER");
    }
  }, [user]);

  useEffect(() => {
    if (reportData.length > 0) {
      processCalendarData(reportData);
    }
  }, [reportData]);

  const processCalendarData = (data) => {
    const grouped = {};
    const employees = [];
    const employeeMap = new Map();

    data.forEach((record) => {
      const employeeName = record.employeeName;

      if (!employeeMap.has(employeeName)) {
        employeeMap.set(employeeName, {
          employeeName: employeeName,
          employeeId: record.employeeId,
          employeeCode: record.employeeCode,
        });
        employees.push(employeeMap.get(employeeName));
      }

      if (!grouped[employeeName]) {
        grouped[employeeName] = {};
      }

      const dateKey = record.attendanceDate || record.atendanceDate;
      if (dateKey) {
        grouped[employeeName][dateKey] = record;
      }
    });

    setReportCalendarData(grouped);
    setReportEmployeeList(
      employees.sort((a, b) => a.employeeName.localeCompare(b.employeeName)),
    );
  };

  const generateAttendanceSummary = (data) => {
    const summary = {};

    data.forEach((record) => {
      const employeeName = record.employeeName;
      if (!summary[employeeName]) {
        summary[employeeName] = {
          employeeName: employeeName,
          departmentName: record.departmentName,
          roleName: record.roleName,
          totalDays: 0,
          presentDays: 0,
          absentDays: 0,
          totalMinutes: 0,
        };
      }

      summary[employeeName].totalDays += 1;

      if (record.punchInTime && record.punchOutTime) {
        summary[employeeName].presentDays += 1;

        const punchInMinutes =
          record.punchInTime.hour * 60 + record.punchInTime.minute;
        const punchOutMinutes =
          record.punchOutTime.hour * 60 + record.punchOutTime.minute;

        let diffMinutes = punchOutMinutes - punchInMinutes;
        if (diffMinutes < 0) {
          diffMinutes += 24 * 60;
        }

        summary[employeeName].totalMinutes += Math.max(0, diffMinutes);
      } else {
        summary[employeeName].absentDays += 1;
      }
    });

    return Object.values(summary).map((stats) => ({
      ...stats,
      attendanceRate:
        ((stats.presentDays / stats.totalDays) * 100).toFixed(1) + "%",
      avgHoursPerDay:
        stats.presentDays > 0
          ? (stats.totalMinutes / stats.presentDays / 60).toFixed(1) + "h"
          : "0h",
    }));
  };

  const handleViewReport = async () => {
    setViewSummary(false);
    setViewCalendar(false);

    if (!viewReport) {
      await fetchCompleteReport();
    }
    setViewReport(!viewReport);
  };

  const handleViewSummary = () => {
    setViewReport(false);
    setViewCalendar(false);

    if (!viewSummary && reportData.length > 0) {
      const summary = generateAttendanceSummary(reportData);
      setSummaryData(summary);
    } else if (!viewSummary && reportData.length === 0) {
      toast.info("Please load the complete report first to view summary");
      return;
    }
    setViewSummary(!viewSummary);
  };

  const handleViewCalendar = async () => {
    setViewReport(false);
    setViewSummary(false);

    if (!viewCalendar) {
      await fetchMonthlyAttendance(new Date());
    }
    setViewCalendar(!viewCalendar);
  };

  const fetchMonthlyAttendance = async (date) => {
    const requestId = ++calendarRequestId.current;
    setLoading(true);
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const formattedDate = `${year}-${String(month).padStart(2, "0")}-01`;

      let endpoint;
      const roleToUse = user?.authorities?.includes("ROLE_ADMIN")
        ? "ADMIN"
        : selectedRole;

      switch (roleToUse) {
        case "ADMIN":
          endpoint = `${process.env.REACT_APP_API_BASE_URL}/attendance/employee-monthly`;
          break;
        case "HR":
          endpoint = `${process.env.REACT_APP_API_HR_URL}/attendance/employee-monthly`;
          break;
        case "MANAGER":
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/attendance/employee-monthly`;
          break;
        default:
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/attendance/employee-monthly`;
      }

      const response = await axiosInstance.get(endpoint, {
        params: {
          attendanceDate: formattedDate,
        },
      });

      // A newer request has already been fired since this one started —
      // discard this (now stale) response instead of overwriting good data.
      if (requestId !== calendarRequestId.current) return;

      if (response.data.status === 200 && response.data.data) {
        const employeeData = response.data.data;
        setEmployeeList(employeeData);

        const calendarFormatted = {};
        employeeData.forEach((emp) => {
          const attendanceByDate = {};
          (emp.monthlyAttendance || []).forEach((att) => {
            attendanceByDate[att.attendanceDate] = att;
          });
          calendarFormatted[emp.employeeName] = attendanceByDate;
        });

        setCalendarData(calendarFormatted);
      }
    } catch (error) {
      if (requestId !== calendarRequestId.current) return;

      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to fetch monthly attendance data";

      setEmployeeList([]);
      setCalendarData({});

      console.error("Error fetching monthly attendance:", error);
    } finally {
      if (requestId === calendarRequestId.current) setLoading(false);
    }
  };

  const fetchSingleEmployeeAttendance = async (date, employeeId) => {
    const requestId = ++singleEmployeeRequestId.current;
    setLoading(true);
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const formattedDate = `${year}-${String(month).padStart(2, "0")}-01`;

      let endpoint;
      const roleToUse = user?.authorities?.includes("ROLE_ADMIN")
        ? "ADMIN"
        : selectedRole;

      switch (roleToUse) {
        case "ADMIN":
          endpoint = `${process.env.REACT_APP_API_BASE_URL}/attendance/employee-monthly`;
          break;
        case "HR":
          endpoint = `${process.env.REACT_APP_API_HR_URL}/attendance/employee-monthly`;
          break;
        case "MANAGER":
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/attendance/employee-monthly`;
          break;
        default:
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/attendance/employee-monthly`;
      }

      const response = await axiosInstance.get(
        employeeId ? `${endpoint}/${employeeId}` : endpoint,
        {
          params: {
            attendanceDate: formattedDate,
          },
        },
      );

      if (requestId !== singleEmployeeRequestId.current) return;

      if (response.data.status === 200 && response.data.data) {
        let employeeData;

        if (Array.isArray(response.data.data)) {
          employeeData = response.data.data.find(
            (emp) => emp.employeeId === parseInt(employeeId),
          );
        } else {
          employeeData = response.data.data;
        }

        if (employeeData) {
          const formattedData = {
            ...employeeData,
            monthlyAttendance: employeeData.monthlyAttendance || [],
            leaveHistories: employeeData.leaveHistories || [],
            workingDays: employeeData.workingDays || null,
          };

          setSingleEmployeeData(formattedData);
        } else {
          console.error("Employee not found in response for ID:", employeeId);
          setSingleEmployeeData(null);
        }
      }
    } catch (error) {
      if (requestId !== singleEmployeeRequestId.current) return;

      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to fetch employee attendance data";

      setSingleEmployeeData(null);

      console.error("Error fetching employee attendance:", error);
    } finally {
      if (requestId === singleEmployeeRequestId.current) setLoading(false);
    }
  };

  const handleEmployeeChange = (date, employeeId) => {
    setCurrentMonth(date);

    if (employeeId && employeeId !== "all") {
      fetchSingleEmployeeAttendance(date, employeeId);
    } else {
      setSingleEmployeeData(null);
      fetchMonthlyAttendance(date);
    }
  };

  const toISODate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Quick date-range presets: All time, Week/Month/Year to date.
  const applyQuickRange = (range) => {
    setQuickRange(range);
    const today = new Date();

    if (range === "all") {
      setStartDateFilter("");
      setEndDateFilter("");
      return;
    }

    let start;
    if (range === "wtd") {
      const day = today.getDay(); // 0 = Sunday
      const diffToMonday = day === 0 ? 6 : day - 1;
      start = new Date(today);
      start.setDate(today.getDate() - diffToMonday);
    } else if (range === "mtd") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
    } else if (range === "ytd") {
      start = new Date(today.getFullYear(), 0, 1);
    }

    setStartDateFilter(toISODate(start));
    setEndDateFilter(toISODate(today));
  };

  const handleManualDateChange = (setter) => (e) => {
    setQuickRange("custom");
    setter(e.target.value);
  };

  // Displays punch times in either 24hr ("18:59") or 12hr ("6:59 PM")
  // format depending on the toolbar toggle.
  const formatTimeDisplay = (timeObj) => {
    if (
      !timeObj ||
      typeof timeObj.hour !== "number" ||
      typeof timeObj.minute !== "number"
    ) {
      return "—";
    }
    return use24Hour
      ? formatTime(timeObj)
      : formatTime12Hour(timeObj.hour, timeObj.minute);
  };

  const formatTime = (timeObj) => {
    if (
      !timeObj ||
      typeof timeObj.hour !== "number" ||
      typeof timeObj.minute !== "number"
    ) {
      return "—";
    }

    return `${timeObj.hour.toString().padStart(2, "0")}:${timeObj.minute
      .toString()
      .padStart(2, "0")}`;
  };

  // Maps the backend's AttendanceTimeStamp enum (EARLY, LATE, ON_TIME,
  // PENDING) to a badge colour class. Punch-in only ever gets flagged red
  // for a late punch-in — every other state (on time, early, pending)
  // renders with no color, never green.
  const getPunchStatusClass = (statusText) => {
    if (!statusText) return "";

    const normalized = statusText
      .toString()
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_");

    switch (normalized) {
      case "LATE":
        return "status-late";
      default:
        return "";
    }
  };

  // Anything punched out before 6:59 PM counts as an early logout and is
  // flagged red. Logging out later than that is normal and gets no
  // special styling.
  const EARLY_LOGOUT_CUTOFF_MINUTES = 18 * 60 + 59; // 6:59 PM

  const isEarlyLogout = (timeObj) => {
    if (
      !timeObj ||
      typeof timeObj.hour !== "number" ||
      typeof timeObj.minute !== "number"
    ) {
      return false;
    }
    const minutes = timeObj.hour * 60 + timeObj.minute;
    return minutes < EARLY_LOGOUT_CUTOFF_MINUTES;
  };

  // Parses API-provided duration labels like "8h 0m", "9h", or "45m" into
  // total minutes, used to compare actual hours worked against the
  // employee's scheduled totalOfficeHours.
  const parseDurationLabelToMinutes = (label) => {
    if (!label || typeof label !== "string") return null;

    const hourMatch = label.match(/(\d+(?:\.\d+)?)\s*h/i);
    const minuteMatch = label.match(/(\d+)\s*m/i);

    if (!hourMatch && !minuteMatch) return null;

    const hours = hourMatch ? parseFloat(hourMatch[1]) : 0;
    const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;

    return hours * 60 + minutes;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "—";
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.warn("Error formatting date:", dateString, error);
      return "—";
    }
  };

  const filteredData = (data || []).filter((item) => {
    const matchesDateRange =
      (startDateFilter === "" ||
        new Date(item.attendanceDate) >= new Date(startDateFilter)) &&
      (endDateFilter === "" ||
        new Date(item.attendanceDate) <= new Date(endDateFilter));

    return matchesDateRange;
  });

  const filteredReportData = (reportData || []).filter((item) => {
    const itemDate = item.attendanceDate || item.atendanceDate;
    const matchesDateRange =
      (startDateFilter === "" ||
        new Date(itemDate) >= new Date(startDateFilter)) &&
      (endDateFilter === "" || new Date(itemDate) <= new Date(endDateFilter));

    const matchesDepartment =
      departmentFilter === "" || item.departmentName === departmentFilter;

    const matchesRole =
      roleNameFilter === "" || item.roleName === roleNameFilter;

    const matchesAttendanceType =
      attendanceTypeFilter === "" ||
      item.attendanceType === attendanceTypeFilter;

    const matchesPunchStatus =
      punchStatusFilter === "all" ||
      (punchStatusFilter === "earlyLogout" &&
        isEarlyLogout(item.punchOutTime)) ||
      (punchStatusFilter === "lateIn" &&
        (item.punchInStatus || "").toString().trim().toUpperCase() === "LATE");

    return (
      matchesDateRange &&
      matchesDepartment &&
      matchesRole &&
      matchesAttendanceType &&
      matchesPunchStatus
    );
  });

  // Dropdown option lists, derived from whatever report data is loaded.
  const departmentOptions = useMemo(() => {
    const set = new Set();
    (reportData || []).forEach((r) => {
      if (r.departmentName) set.add(r.departmentName);
    });
    return Array.from(set).sort();
  }, [reportData]);

  const roleOptions = useMemo(() => {
    const set = new Set();
    (reportData || []).forEach((r) => {
      if (r.roleName) set.add(r.roleName);
    });
    return Array.from(set).sort();
  }, [reportData]);

  const attendanceTypeOptions = useMemo(() => {
    const set = new Set();
    (reportData || []).forEach((r) => {
      if (r.attendanceType) set.add(r.attendanceType);
    });
    return Array.from(set).sort();
  }, [reportData]);

  // Small stat cards shown above whichever view is active.
  const reportStats = useMemo(() => {
    const total = filteredReportData.length;
    let present = 0;
    let lateIn = 0;
    let earlyOut = 0;
    let shortHours = 0;

    filteredReportData.forEach((row) => {
      if (row.punchInTime && row.punchOutTime) present += 1;

      if (
        (row.punchInStatus || "").toString().trim().toUpperCase() === "LATE"
      ) {
        lateIn += 1;
      }

      if (isEarlyLogout(row.punchOutTime)) earlyOut += 1;

      if (row.punchInTime && row.punchOutTime) {
        const inMin = row.punchInTime.hour * 60 + row.punchInTime.minute;
        let outMin = row.punchOutTime.hour * 60 + row.punchOutTime.minute;
        let diffMinutes = outMin - inMin;
        if (diffMinutes < 0) diffMinutes += 24 * 60;

        const officeMinutes = parseDurationLabelToMinutes(row.totalOfficeHours);
        if (officeMinutes !== null && diffMinutes < officeMinutes) {
          shortHours += 1;
        }
      }
    });

    return {
      total,
      present,
      absent: total - present,
      lateIn,
      earlyOut,
      shortHours,
    };
  }, [filteredReportData]);

  const punchoutStats = useMemo(() => {
    const total = (data || []).length;
    return {
      total,
      pending: total,
      selected: selectedAttendance.size,
    };
  }, [data, selectedAttendance]);

  // Bulk selection handlers
  const handleToggleSelect = (attendanceId) => {
    setSelectedAttendance((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(attendanceId)) {
        newSet.delete(attendanceId);
      } else {
        newSet.add(attendanceId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedAttendance.size === data.length) {
      setSelectedAttendance(new Set());
    } else {
      setSelectedAttendance(new Set(data.map((item) => item.attendanceId)));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedAttendance.size === 0) {
      toast.warning("Please select at least one attendance request");
      return;
    }

    const payload = data
      .filter((item) => selectedAttendance.has(item.attendanceId))
      .map((record) => {
        if (!record.punchOutTime) {
          throw new Error(`Punch-out time missing for ${record.employeeName}`);
        }

        return {
          attendanceId: record.attendanceId,
          punchOutTime: formatTime12Hour(
            record.punchOutTime.hour,
            record.punchOutTime.minute,
          ),
        };
      });

    setLoading(true);
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_MANAGER_URL}/attendance/approve-punchout/bulk`,
        payload,
      );

      setData((prev) =>
        prev.filter((item) => !selectedAttendance.has(item.attendanceId)),
      );
      setSelectedAttendance(new Set());

      toast.success(`Approved ${payload.length} attendance requests`);
      decreaseNotificationCount(payload.length);
      onRequestProcessed?.();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPunchOut = (record) => {
    setSelectedRecord(record);
    setEditMode("punchout");

    if (record.punchInTime) {
      setEditPunchInTime(timeObjectToString(record.punchInTime));
    } else {
      setEditPunchInTime("09:00");
    }

    if (record.punchOutTime) {
      setEditPunchOutTime(timeObjectToString(record.punchOutTime));
    } else {
      setEditPunchOutTime("18:00");
    }

    setShowEditModal(true);
  };

  const handleEditBothTimes = (record) => {
    setSelectedRecord(record);
    setEditMode("both");

    if (record.punchInTime) {
      setEditPunchInTime(timeObjectToString(record.punchInTime));
    } else {
      setEditPunchInTime("09:00");
    }

    if (record.punchOutTime) {
      setEditPunchOutTime(timeObjectToString(record.punchOutTime));
    } else {
      setEditPunchOutTime("18:00");
    }

    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedRecord(null);
    setEditPunchOutTime("");
    setEditPunchInTime("");
    setEditMode("punchout");
  };

  const getDiffMinutes = (inMin, outMin) => {
    let diff = outMin - inMin;
    if (diff < 0) diff += 24 * 60; // night shift support
    return diff;
  };

  const validateTimes = () => {
    const punchInObj = timeStringToObject(editPunchInTime);
    const punchOutObj = timeStringToObject(editPunchOutTime);

    // basic bounds check
    if (
      punchInObj.hour < 0 ||
      punchInObj.hour > 23 ||
      punchInObj.minute < 0 ||
      punchInObj.minute > 59
    ) {
      toast.error("Invalid punch-in time");
      return false;
    }

    if (
      punchOutObj.hour < 0 ||
      punchOutObj.hour > 23 ||
      punchOutObj.minute < 0 ||
      punchOutObj.minute > 59
    ) {
      toast.error("Invalid punch-out time");
      return false;
    }

    let punchInMinutes;
    let punchOutMinutes;

    if (editMode === "both") {
      punchInMinutes = punchInObj.hour * 60 + punchInObj.minute;
      punchOutMinutes = punchOutObj.hour * 60 + punchOutObj.minute;
    } else {
      if (!selectedRecord || !selectedRecord.punchInTime) {
        toast.error("Punch-in time is required for validation");
        return false;
      }

      punchInMinutes =
        selectedRecord.punchInTime.hour * 60 +
        selectedRecord.punchInTime.minute;
      punchOutMinutes = punchOutObj.hour * 60 + punchOutObj.minute;
    }

    const diffMinutes = getDiffMinutes(punchInMinutes, punchOutMinutes);
    const diffHours = diffMinutes / 60;

    if (diffMinutes <= 0) {
      toast.error("Invalid work duration");
      return false;
    }

    if (diffHours > 16) {
      toast.error("Work duration cannot exceed 16 hours");
      return false;
    }

    if (diffHours < 0.5) {
      toast.error("Work duration must be at least 30 minutes");
      return false;
    }

    return true;
  };

  const handleApproveWithEdit = async () => {
    if (!validateTimes()) {
      return;
    }

    setLoading(true);
    try {
      const punchOutObj = timeStringToObject(editPunchOutTime);

      const formattedPunchOutTime = formatTime12Hour(
        punchOutObj.hour,
        punchOutObj.minute,
      );

      const payload = {
        attendanceId: selectedRecord.attendanceId,
        punchOutTime: formattedPunchOutTime,
      };

      if (editMode === "both") {
        const punchInObj = timeStringToObject(editPunchInTime);
        const formattedPunchInTime = formatTime12Hour(
          punchInObj.hour,
          punchInObj.minute,
        );
        payload.punchInTime = formattedPunchInTime;
      }

      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_MANAGER_URL}/attendance/approve-punchout`,
        payload,
      );

      if (response.data.status === 200) {
        setData(
          data.filter(
            (item) => item.attendanceId !== selectedRecord.attendanceId,
          ),
        );
        toast.success(
          `Attendance request approved successfully with ${
            editMode === "both" ? "updated times" : "updated punch-out time"
          }`,
        );
        handleCloseModal();

        decreaseNotificationCount();

        if (onRequestProcessed && typeof onRequestProcessed === "function") {
          onRequestProcessed();
        }
      } else {
        toast.error("Failed to approve attendance request");
      }
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Error approving attendance request";

      if (status === 409) {
        toast.error(message);
      } else {
        console.error("Error approving attendance:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (attendanceId) => {
    setLoading(true);
    try {
      const record = data.find((item) => item.attendanceId === attendanceId);

      if (!record || !record.punchOutTime) {
        toast.error("Unable to approve: Punch-out time not found");
        return;
      }

      const formattedPunchOutTime = formatTime12Hour(
        record.punchOutTime.hour,
        record.punchOutTime.minute,
      );

      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_MANAGER_URL}/attendance/approve-punchout`,
        {
          attendanceId: attendanceId,
          punchOutTime: formattedPunchOutTime,
        },
      );

      if (response.data.status === 200) {
        setData(data.filter((item) => item.attendanceId !== attendanceId));
        toast.success("Punchout request approved successfully");

        decreaseNotificationCount();

        if (onRequestProcessed && typeof onRequestProcessed === "function") {
          onRequestProcessed();
        }
      } else {
        toast.error("Failed to approve punchout request");
      }
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Error approving punchout request";

      if (status === 409) {
        toast.error(message);
      } else {
        console.error("Error approving punchout:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Both the punchout-table row action and the calendar's delete button
  // route through here: they just record *what* to delete and open the
  // warning popup; the actual API call happens in confirmDeleteAttendance
  // once the user accepts.
  const [deleteTarget, setDeleteTarget] = useState(null); // { source: "table" | "calendar", attendanceId }
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  const handleDeleteAttendance = (attendanceId) => {
    if (!attendanceId) return;

    if (!canDeleteAttendance) {
      toast.error("You do not have permission to delete attendance records");
      return;
    }

    setDeleteTarget({ source: "table", attendanceId });
    setShowDeleteWarning(true);
  };

  // Same delete action as above, but for a record surfaced in the team
  // Calendar tab (EnhancedAttendanceCalendar) rather than the punchout
  // table — that view's data (calendarData/employeeList) lives in
  // different state, so on success we refetch it instead of filtering
  // `data`.
  const handleDeleteCalendarAttendance = (record) => {
    const attendanceId = record?.attendanceId;
    if (!attendanceId) return;

    if (!canDeleteAttendance) {
      toast.error("You do not have permission to delete attendance records");
      return;
    }

    setDeleteTarget({ source: "calendar", attendanceId });
    setShowDeleteWarning(true);
  };

  const cancelDeleteAttendance = () => {
    setShowDeleteWarning(false);
    setDeleteTarget(null);
  };

  const confirmDeleteAttendance = async () => {
    if (!deleteTarget) return;
    const { source, attendanceId } = deleteTarget;

    setShowDeleteWarning(false);

    const isAdmin = user?.authorities?.includes("ROLE_ADMIN");

    // Admin and HR hit different base paths (/api/admin vs /api/hr);
    // admin takes priority when a user holds both roles.
    const endpoint = isAdmin
      ? `${process.env.REACT_APP_API_BASE_URL}/attendance/${attendanceId}`
      : `${process.env.REACT_APP_API_HR_URL}/attendance/${attendanceId}`;

    setLoading(true);
    try {
      const response = await axiosInstance.delete(endpoint);

      if (response.data.status === 200) {
        toast.success("Attendance record deleted successfully");

        if (source === "table") {
          setData((prev) =>
            prev.filter((item) => item.attendanceId !== attendanceId),
          );
          setSelectedAttendance((prev) => {
            const newSet = new Set(prev);
            newSet.delete(attendanceId);
            return newSet;
          });

          if (onRequestProcessed && typeof onRequestProcessed === "function") {
            onRequestProcessed();
          }
        } else {
          if (selectedEmployeeId && selectedEmployeeId !== "all") {
            fetchSingleEmployeeAttendance(currentMonth, selectedEmployeeId);
          } else {
            fetchMonthlyAttendance(currentMonth);
          }
        }
      } else {
        toast.error("Failed to delete attendance record");
      }
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Error deleting attendance record";

      if (status === 403) {
        toast.error("You are not authorized to delete this record");
      } else if (status === 404) {
        toast.error("Attendance record not found");
      } else {
        toast.error(message);
      }
      console.error("Error deleting attendance:", error);
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const punchoutColumns = [
    { header: "Select", accessor: "select" },
    { header: "Employee", accessor: "employeeName" },
    { header: "Date", accessor: "attendanceDate" },
    { header: "Location", accessor: "location" },
    { header: "Punch In", accessor: "punchInTime" },
    { header: "Punch In Message", accessor: "punchInMessage" },
    { header: "Punch Out", accessor: "punchOutTime" },
    { header: "Punch Out Message", accessor: "punchOutMessage" },
    { header: "Status", accessor: "punchoutStatus" },
    { header: "Attendance Status", accessor: "attendanceStatus" },
    { header: "Manager Message", accessor: "managerRequestMessage" },
    { header: "Work Tasks", accessor: "workTasks" },
    { header: "Actions", accessor: "actions" },
  ];

  const reportColumns = [
    { header: "Employee", accessor: "employeeName" },
    { header: "Date", accessor: "attendanceDate" },
    { header: "Department", accessor: "departmentName" },
    { header: "Role", accessor: "roleName" },
    { header: "Attendance Type", accessor: "attendanceType" },
    { header: "Punch In", accessor: "punchInTime" },
    { header: "Punch Out", accessor: "punchOutTime" },
    { header: "Hours Worked", accessor: "hoursWorked" },
  ];

  const summaryColumns = [
    { header: "Employee", accessor: "employeeName" },
    { header: "Department", accessor: "departmentName" },
    { header: "Role", accessor: "roleName" },
    { header: "Total Days", accessor: "totalDays" },
    { header: "Present Days", accessor: "presentDays" },
    { header: "Absent Days", accessor: "absentDays" },
    { header: "Attendance Rate", accessor: "attendanceRate" },
    { header: "Avg Hours/Day", accessor: "avgHoursPerDay" },
  ];

  const tableData = filteredData.map((row) => ({
    select: (
      <input
        type="checkbox"
        checked={selectedAttendance.has(row.attendanceId)}
        onChange={() => handleToggleSelect(row.attendanceId)}
        style={{ cursor: "pointer", width: "18px", height: "18px" }}
      />
    ),
    employeeName: row.employeeName,
    attendanceDate: formatDate(row.attendanceDate),
    location: (
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        {row.punchInLatitude && row.punchInLongitude && (
          <MiniLocationMap
            latitude={row.punchInLatitude}
            longitude={row.punchInLongitude}
            label="In"
            onClick={() => handleLocationClick(row)}
          />
        )}
        {row.punchOutLatitude && row.punchOutLongitude && (
          <MiniLocationMap
            latitude={row.punchOutLatitude}
            longitude={row.punchOutLongitude}
            label="Out"
            onClick={() => handleLocationClick(row)}
          />
        )}
        {!row.punchInLatitude && !row.punchOutLatitude && (
          <span style={{ color: "#999", fontSize: "12px" }}>No location</span>
        )}
      </div>
    ),
    punchInTime: formatTimeDisplay(row.punchInTime),
    punchInMessage: row.punchInMessage || "—",
    punchOutTime: formatTimeDisplay(row.punchOutTime),
    punchOutMessage: row.punchOutMessage || "—",
    punchoutStatus: <StatusCard status={row.punchoutStatus} />,
    attendanceStatus: (
      <StatusCard status={row.attendanceStatus ? "Active" : "Inactive"} />
    ),
    managerRequestMessage: row.managerRequestMessage || "—",
    workTasks: row.workTasks || "—",
    actions: (
      <div className="action-buttons">
        <IconButton
          className="icon-button--edit"
          icon="mdi:pencil"
          onClick={() => handleEditPunchOut(row)}
          disabled={loading}
          title="Edit Punch-out Time"
        />
        <IconButton
          className="icon-button--add"
          icon="mdi:check"
          onClick={() => handleApprove(row.attendanceId)}
          disabled={loading}
          title="Quick Approve"
        />
        <IconButton
          className="icon-button--edit"
          icon="mdi:clock-edit"
          onClick={() => handleEditBothTimes(row)}
          disabled={loading}
          title="Edit Both Times"
          style={{ background: "linear-gradient(135deg, #8b5cf6, #7c3aed)" }}
        />
        {canDeleteAttendance && (
          <IconButton
            className="icon-button--delete"
            icon="mdi:trash-can"
            onClick={() => handleDeleteAttendance(row.attendanceId)}
            disabled={loading}
            title="Delete Attendance Record"
            style={{ background: "linear-gradient(135deg, #ef4444, #dc2626)" }}
          />
        )}
      </div>
    ),
  }));

  const reportTableData = filteredReportData.map((row) => {
    const punchIn = row.punchInTime;
    const punchOut = row.punchOutTime;
    let hoursWorked = "—";
    let isShortOfOfficeHours = false;

    if (punchIn && punchOut) {
      const punchInMinutes = punchIn.hour * 60 + punchIn.minute;
      const punchOutMinutes = punchOut.hour * 60 + punchOut.minute;

      let diffMinutes = punchOutMinutes - punchInMinutes;
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60;
      }
      diffMinutes = Math.max(0, diffMinutes);

      const hours = diffMinutes / 60;
      hoursWorked = hours.toFixed(1) + "h";

      // Working more than (or exactly) the scheduled office hours is fine.
      // Only flag it when the employee worked *less* than expected.
      const officeMinutes = parseDurationLabelToMinutes(row.totalOfficeHours);
      if (officeMinutes !== null && diffMinutes < officeMinutes) {
        isShortOfOfficeHours = true;
      }
    }

    const earlyLogout = isEarlyLogout(punchOut);

    return {
      employeeName: row.employeeName,
      attendanceDate: formatDate(row.attendanceDate),
      departmentName: row.departmentName,
      roleName: row.roleName || "—",
      attendanceType: row.attendanceType || "—",
      punchInTime: row.punchInTime ? (
        <span
          className={`status-badge ${getPunchStatusClass(row.punchInStatus)}`}
          title={row.punchInStatus || ""}
        >
          {formatTimeDisplay(row.punchInTime)}
        </span>
      ) : (
        "—"
      ),
      // Early logout (before 6:59 PM) is flagged red. A late logout gets
      // no special styling — just the plain time.
      punchOutTime: row.punchOutTime ? (
        earlyLogout ? (
          <span className="text-alert" title="Early logout">
            {formatTimeDisplay(row.punchOutTime)}
          </span>
        ) : (
          formatTimeDisplay(row.punchOutTime)
        )
      ) : (
        "—"
      ),
      // Hours worked below the scheduled office hours is flagged red.
      // Meeting or exceeding the scheduled hours is fine — no color.
      hoursWorked: isShortOfOfficeHours ? (
        <span
          className="text-alert"
          title={`Below scheduled office hours (${row.totalOfficeHours || "—"})`}
        >
          {hoursWorked}
        </span>
      ) : (
        hoursWorked
      ),
    };
  });

  const summaryTableData = summaryData.map((row) => ({
    employeeName: row.employeeName,
    departmentName: row.departmentName,
    roleName: row.roleName,
    totalDays: row.totalDays,
    presentDays: row.presentDays,
    absentDays: row.absentDays,
    attendanceRate: row.attendanceRate,
    avgHoursPerDay: row.avgHoursPerDay,
  }));

  const parseTimeString = (timeString) => {
    if (!timeString || timeString === "—") return null;

    const timeRegex = /^(\d{1,2}):(\d{2})\s*(am|pm)$/i;
    const match = timeString.toLowerCase().trim().match(timeRegex);

    if (match) {
      let hour = parseInt(match[1]);
      const minute = parseInt(match[2]);
      const period = match[3].toLowerCase();

      if (period === "pm" && hour !== 12) {
        hour += 12;
      } else if (period === "am" && hour === 12) {
        hour = 0;
      }

      return { hour, minute };
    }

    const time24Regex = /^(\d{1,2}):(\d{2})$/;
    const match24 = timeString.trim().match(time24Regex);

    if (match24) {
      const hour = parseInt(match24[1]);
      const minute = parseInt(match24[2]);

      if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
        return { hour, minute };
      }
    }

    console.warn("Failed to parse time string:", timeString);
    return null;
  };

  const transformAttendanceData = (data) => {
    return data.map((record) => {
      const punchInParsed = parseTimeString(record.punchInTime);
      const punchOutParsed = parseTimeString(record.punchOutTime);

      return {
        ...record,
        attendanceDate: record.attendanceDate || record.atendanceDate,
        punchInTime: punchInParsed,
        punchOutTime: punchOutParsed,
        departmentName: Array.isArray(record.departmentName)
          ? record.departmentName.join(", ").replace(/,\s*$/, "")
          : record.departmentName || "—",
      };
    });
  };

  const fetchCompleteReport = async (roleOverride = null) => {
    const roleToUse = user?.authorities?.includes("ROLE_ADMIN")
      ? "ADMIN"
      : roleOverride || selectedRole;

    setLoading(true);
    try {
      let endpoint;

      switch (roleToUse) {
        case "ADMIN":
          endpoint = `${process.env.REACT_APP_API_BASE_URL}/attendance/All`;
          break;
        case "HR":
          endpoint = `${process.env.REACT_APP_API_HR_URL}/attendance/All`;
          break;
        case "MANAGER":
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/attendance/All`;
          break;
        default:
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/attendance/All`;
      }

      const response = await axiosInstance.get(endpoint);
      const transformedData = transformAttendanceData(response.data.data || []);
      setReportData(transformedData);
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to fetch complete attendance report";

      setReportData([]);

      if (status === 409) {
        toast.error(message);
      } else {
        console.error("Error fetching complete report:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSwitch = async (role) => {
    if (role === selectedRole) return;

    setSelectedRole(role);
    setSummaryData([]);

    await fetchCompleteReport(role);
  };

  const calculateWorkDuration = () => {
    const punchInObj = timeStringToObject(editPunchInTime);
    const punchOutObj = timeStringToObject(editPunchOutTime);

    const inMin = punchInObj.hour * 60 + punchInObj.minute;
    const outMin = punchOutObj.hour * 60 + punchOutObj.minute;

    let diff = outMin - inMin;
    if (diff < 0) diff += 24 * 60;

    if (diff <= 0) return "Invalid duration";

    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    return `${hours}h ${minutes}m`;
  };

  const activeStats = viewReport ? reportStats : punchoutStats;

  return (
    <div className="manage-attendance-container">
      <div className="attendance-toolbar">
        <div className="toolbar-top-row">
          <div className="toolbar-title">
            <h1 className="display5">Manage Team Attendance</h1>
          </div>

          <div className="view-toggle">
            <button
              className={`toggle-item ${viewReport ? "active" : ""}`}
              onClick={handleViewReport}
              disabled={loading}
            >
              <Icon icon="ph:table-light" width="18" />
              <span>Report</span>
            </button>

            <button
              className={`toggle-item ${viewSummary ? "active" : ""}`}
              onClick={handleViewSummary}
              disabled={loading}
            >
              <Icon icon="mdi:chart-bar" width="18" />
              <span>Summary</span>
            </button>

            <button
              className={`toggle-item ${viewCalendar ? "active" : ""}`}
              onClick={handleViewCalendar}
              disabled={loading}
            >
              <Icon icon="uil:calendar-alt" width="18" />
              <span>Calendar</span>
            </button>
          </div>
        </div>

        {!viewCalendar && (
          <div className="toolbar-filters-row">
            <div className="filter-pill-group">
              {[
                { key: "all", label: "All" },
                { key: "wtd", label: "WTD" },
                { key: "mtd", label: "MTD" },
                { key: "ytd", label: "YTD" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  className={`filter-pill-btn ${
                    quickRange === opt.key ? "active" : ""
                  }`}
                  onClick={() => applyQuickRange(opt.key)}
                  type="button"
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="filter-pill">
              <Icon icon="mdi:calendar-start" width="16" />
              <input
                type="date"
                aria-label="From date"
                value={startDateFilter}
                onChange={handleManualDateChange(setStartDateFilter)}
              />
            </div>

            <div className="filter-pill">
              <Icon icon="mdi:calendar-end" width="16" />
              <input
                type="date"
                aria-label="To date"
                value={endDateFilter}
                onChange={handleManualDateChange(setEndDateFilter)}
              />
            </div>

            <div className="filter-pill-group">
              {[
                { key: false, label: "12h" },
                { key: true, label: "24h" },
              ].map((opt) => (
                <button
                  key={opt.label}
                  className={`filter-pill-btn ${
                    use24Hour === opt.key ? "active" : ""
                  }`}
                  onClick={() => setUse24Hour(opt.key)}
                  type="button"
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {viewReport && (
              <>
                <div className="filter-pill">
                  <select
                    aria-label="Department"
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                  >
                    <option value="">All Departments</option>
                    {departmentOptions.map((dep) => (
                      <option key={dep} value={dep}>
                        {dep}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-pill">
                  <select
                    aria-label="Role"
                    value={roleNameFilter}
                    onChange={(e) => setRoleNameFilter(e.target.value)}
                  >
                    <option value="">All Roles</option>
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-pill">
                  <select
                    aria-label="Attendance type"
                    value={attendanceTypeFilter}
                    onChange={(e) => setAttendanceTypeFilter(e.target.value)}
                  >
                    <option value="">All Types</option>
                    {attendanceTypeOptions.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-pill-group">
                  {[
                    { key: "all", label: "All" },
                    { key: "lateIn", label: "Late In" },
                    { key: "earlyLogout", label: "Early Out" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      className={`filter-pill-btn ${
                        punchStatusFilter === opt.key ? "active" : ""
                      }`}
                      onClick={() => setPunchStatusFilter(opt.key)}
                      type="button"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {availableRoles.length > 1 && (
                  <div className="role-toggle-buttons">
                    {availableRoles.map((role) => (
                      <button
                        key={role}
                        className={`role-toggle-btn ${
                          selectedRole === role ? "active" : ""
                        }`}
                        onClick={() => handleRoleSwitch(role)}
                        disabled={loading}
                      >
                        {role === "ADMIN"
                          ? "Admin View"
                          : role === "HR"
                            ? "HR View"
                            : "Manager View"}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!viewCalendar && (
          <div className="toolbar-stats-row">
            {viewReport ? (
              <>
                <div className="stat-card">
                  <div className="stats-card__accent" aria-hidden="true" />
                  <span className="stat-value">{reportStats.total}</span>
                  <span className="stat-label">Total</span>
                </div>
                <div className="stat-card">
                  <div className="stats-card__accent" aria-hidden="true" />
                  <span className="stat-value">{reportStats.present}</span>
                  <span className="stat-label">Present</span>
                </div>
                <div className="stat-card">
                  <div className="stats-card__accent" aria-hidden="true" />
                  <span className="stat-value">{reportStats.absent}</span>
                  <span className="stat-label">Absent</span>
                </div>
                <div className="stat-card">
                  <div className="stats-card__accent" aria-hidden="true" />
                  <span className="stat-value">{reportStats.lateIn}</span>
                  <span className="stat-label">Late In</span>
                </div>
                <div className="stat-card stat-card--alert">
                  <div className="stats-card__accent" aria-hidden="true" />
                  <span className="stat-value">{reportStats.earlyOut}</span>
                  <span className="stat-label">Early Out</span>
                </div>
                <div className="stat-card stat-card--alert">
                  <div className="stats-card__accent" aria-hidden="true" />
                  <span className="stat-value">{reportStats.shortHours}</span>
                  <span className="stat-label">Short Hours</span>
                </div>
              </>
            ) : (
              <>
                <div className="stat-card">
                  <div className="stats-card__accent" aria-hidden="true" />
                  <span className="stat-value">{punchoutStats.total}</span>
                  <span className="stat-label">Total</span>
                </div>
                <div className="stat-card">
                  <div className="stats-card__accent" aria-hidden="true" />
                  <span className="stat-value">{punchoutStats.pending}</span>
                  <span className="stat-label">Pending</span>
                </div>
                <div className="stat-card">
                  <div className="stats-card__accent" aria-hidden="true" />
                  <span className="stat-value">{punchoutStats.selected}</span>
                  <span className="stat-label">Selected</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="manage-attendance-content">
        {!viewReport && !viewSummary && !viewCalendar && (
          <div className="attendance-section">
            {loading && (
              <div className="loading-indicator">
                <LoadingScreen />
              </div>
            )}

            {data.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginBottom: "16px",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={handleSelectAll}
                  style={{
                    padding: "8px 16px",
                    backgroundColor:
                      selectedAttendance.size === data.length
                        ? "#e0e0e0"
                        : "#f5f5f5",
                    border: "1px solid #ccc",
                    borderRadius: "20px",
                    cursor: "pointer",
                    fontSize: "14px",
                    marginTop: "10px",
                  }}
                >
                  {selectedAttendance.size === data.length
                    ? "Deselect All"
                    : "Select All"}
                </button>

                {selectedAttendance.size > 0 && (
                  <>
                    <span style={{ color: "#666", fontSize: "14px" }}>
                      {selectedAttendance.size} selected
                    </span>
                    <IconButton
                      className="icon-button--add"
                      icon="mdi:check-all"
                      onClick={handleBulkApprove}
                      disabled={loading}
                      style={{ marginLeft: "auto" }}
                    >
                      Approve Selected ({selectedAttendance.size})
                    </IconButton>
                  </>
                )}
              </div>
            )}

            <div className="table-container">
              <GeneralTable
                columns={punchoutColumns}
                data={tableData}
                title="Manage Team Attendance"
              />
            </div>
          </div>
        )}

        {viewReport && (
          <div className="attendance-section">
            {loading && (
              <div className="loading-indicator">
                <LoadingScreen />
              </div>
            )}
            <div className="table-container">
              <GeneralTable
                columns={reportColumns}
                data={reportTableData}
                title="Attendance Report"
              />
            </div>
          </div>
        )}

        {viewSummary && (
          <div className="attendance-section">
            <h2 className="display6">Attendance Summary</h2>
            <div className="table-container">
              <GeneralTable
                columns={summaryColumns}
                data={summaryTableData}
                title="Attendance Summary"
              />
            </div>
          </div>
        )}

        {viewCalendar && (
          <div className="attendance-section">
            <div
              style={{
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "20px",
                justifyContent: "space-between", // ← add this
              }}
            >
              {/* Left: employee selector */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#374151",
                    minWidth: "120px",
                    flexShrink: 0,
                  }}
                >
                  Select Employee:
                </label>
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => {
                    const newEmployeeId = e.target.value;
                    setSelectedEmployeeId(newEmployeeId);
                    handleEmployeeChange(currentMonth, newEmployeeId);
                  }}
                  style={{
                    padding: "10px 16px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                    background: "#fff",
                    color: "#1f2937",
                    cursor: "pointer",
                    minWidth: "250px",
                    maxWidth: "350px",
                    flex: "1",
                    transition: "all 0.2s",
                    outline: "none",
                  }}
                >
                  <option value="all">All Employees</option>
                  {employeeList.map((emp) => (
                    <option key={emp.employeeId} value={emp.employeeId}>
                      {emp.employeeName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Right: export button */}
              <button
                className="toggle-item"
                onClick={() =>
                  exportCalendarToExcel(
                    employeeList,
                    calendarData,
                    currentMonth,
                    currentMonth.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    }),
                  )
                }
                disabled={loading || employeeList.length === 0}
                title="Export calendar as Excel"
              >
                <Icon icon="mdi:microsoft-excel" width="18" />
                <span>Export Excel</span>
              </button>
            </div>

            {selectedEmployeeId &&
            selectedEmployeeId !== "all" &&
            singleEmployeeData ? (
              <EnhancedReusableCalendar
                apiEndpoint={
                  user?.authorities?.includes("ROLE_ADMIN")
                    ? `${process.env.REACT_APP_API_BASE_URL}/attendance/employee-monthly`
                    : selectedRole === "HR"
                      ? `${process.env.REACT_APP_API_HR_URL}/attendance/employee-monthly`
                      : `${process.env.REACT_APP_API_MANAGER_URL}/attendance/employee-monthly`
                }
                employeeId={selectedEmployeeId}
                expectedHoursPerDay={9}
                expectedDaysPerWeek={6}
                showEmployeeSelector={false}
                key={`calendar-${selectedEmployeeId}-${currentMonth.getTime()}`}
              />
            ) : (
              <EnhancedAttendanceCalendar
                calendarData={calendarData}
                employeeList={employeeList}
                onMonthChange={handleEmployeeChange}
                singleEmployeeData={null}
                selectedEmployeeId={selectedEmployeeId}
                setSelectedEmployeeId={setSelectedEmployeeId}
                currentMonth={currentMonth}
                showEmployeeSelector={false}
                canDelete={canDeleteAttendance}
                onDeleteRecord={handleDeleteCalendarAttendance}
              />
            )}
          </div>
        )}
      </div>

      {showEditModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editMode === "both"
                  ? "Edit Attendance Times"
                  : "Edit Punch-out Time"}
              </h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="employee-info">
                <p>
                  <strong>Employee:</strong> {selectedRecord?.employeeName}
                </p>
                <p>
                  <strong>Date:</strong>{" "}
                  {formatDate(selectedRecord?.attendanceDate)}
                </p>
                {editMode === "punchout" && (
                  <p>
                    <strong>Current Punch In:</strong>{" "}
                    {formatTime(selectedRecord?.punchInTime)}
                  </p>
                )}
              </div>

              {editMode === "both" && (
                <div className="time-input-container">
                  <div className="time-input-group">
                    <label htmlFor="edit-punch-in-time">Punch-in Time</label>
                    <InputField
                      id="edit-punch-in-time"
                      type="time"
                      value={editPunchInTime}
                      onChange={(e) => setEditPunchInTime(e.target.value)}
                      className="time-input"
                    />
                  </div>
                </div>
              )}

              <div className="time-input-container">
                <div className="time-input-group">
                  <label htmlFor="edit-punch-out-time">Punch-out Time</label>
                  <InputField
                    id="edit-punch-out-time"
                    type="time"
                    value={editPunchOutTime}
                    onChange={(e) => setEditPunchOutTime(e.target.value)}
                    className="time-input"
                  />
                </div>
              </div>

              <div className="preview-time">
                {editMode === "both" ? (
                  <>
                    <p>
                      <strong>New Punch-in Time:</strong>{" "}
                      {editPunchInTime
                        ? formatTime12Hour(
                            timeStringToObject(editPunchInTime).hour,
                            timeStringToObject(editPunchInTime).minute,
                          )
                        : "—"}
                    </p>
                    <p>
                      <strong>New Punch-out Time:</strong>{" "}
                      {editPunchOutTime
                        ? formatTime12Hour(
                            timeStringToObject(editPunchOutTime).hour,
                            timeStringToObject(editPunchOutTime).minute,
                          )
                        : "—"}
                    </p>
                    <p>
                      <strong>Work Duration:</strong> {calculateWorkDuration()}
                    </p>
                  </>
                ) : (
                  <p>
                    <strong>New Punch-out Time:</strong>{" "}
                    {editPunchOutTime
                      ? formatTime12Hour(
                          timeStringToObject(editPunchOutTime).hour,
                          timeStringToObject(editPunchOutTime).minute,
                        )
                      : "—"}
                  </p>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-button modal-button--cancel"
                onClick={handleCloseModal}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="modal-button modal-button--approve"
                onClick={handleApproveWithEdit}
                disabled={loading}
              >
                {loading ? "Approving..." : `Approve `}
              </button>
            </div>
          </div>
        </div>
      )}

      <LocationModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        record={selectedLocationRecord}
      />

      <WarningPopup
        isOpen={showDeleteWarning}
        onClose={cancelDeleteAttendance}
        onDecline={cancelDeleteAttendance}
        onAccept={confirmDeleteAttendance}
        title="Delete Attendance Record"
        message="Are you sure you want to delete this attendance record? This action cannot be undone."
        acceptText="Delete"
        declineText="Cancel"
      />
    </div>
  );
}

export default ManageAttendance;
